import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiSave, FiArrowLeft, FiLoader, FiCpu, FiSettings } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { configuracaoIAAPI } from '../services/api';
import theme from '../styles/theme';
import Layout from '../components/Layout';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  max-width: 900px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};
`;

const BackButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: transparent;
  border: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  margin: 0;
`;

const Form = styled.form`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const SectionTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const FormGroupFull = styled(FormGroup)`
  grid-column: 1 / -1;
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const Input = styled.input`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  option {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.text.primary};
  }
`;

const Textarea = styled.textarea`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 120px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }
`;

const HelpText = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.xs};
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  justify-content: flex-end;
  padding-top: ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.border.primary};
`;

const Button = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => {
    if (props.$primary) return theme.colors.primary[500];
    return theme.colors.border.primary;
  }};
  background: ${props => {
    if (props.$primary) return theme.colors.primary[500];
    return 'transparent';
  }};
  color: ${props => {
    if (props.$primary) return theme.colors.text.primary;
    return theme.colors.text.secondary;
  }};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  min-width: 120px;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${props => {
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.background.primary;
    }};
    color: ${props => {
      if (props.$primary) return theme.colors.text.primary;
      return theme.colors.primary[500];
    }};
    border-color: ${props => {
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.primary[500];
    }};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonIcon = styled.div`
  font-size: ${theme.fontSizes.base};
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error[500]}20;
  border: 1px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.error[600]};
  font-size: ${theme.fontSizes.sm};
`;

const SuccessMessage = styled.div`
  background: ${theme.colors.success[500]}20;
  border: 1px solid ${theme.colors.success[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.success[600]};
  font-size: ${theme.fontSizes.sm};
`;

const InfoBox = styled.div`
  background: ${theme.colors.primary[500]}10;
  border: 1px solid ${theme.colors.primary[500]}40;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
`;

const InfoIcon = styled.div`
  color: ${theme.colors.primary[500]};
  font-size: ${theme.fontSizes.lg};
  flex-shrink: 0;
`;

const ConfiguracaoIAForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    ia_system_prompt: '',
    ia_tone: 'formal',
    emojis: 'LOW',
    responder_por_audio: true,
    grupo_resumo_conversa: '',
    agent_name: ''
  });

  const [clienteNome, setClienteNome] = useState('');
  const [tones, setTones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [idCliente, setIdCliente] = useState(null);

  // Carregar dados do parâmetro
  useEffect(() => {
    if (id) {
      carregarParametro();
    }
  }, [id]);

  // Carregar tones
  useEffect(() => {
    carregarTones();
  }, []);

  const carregarParametro = async () => {
    try {
      setLoadingData(true);
      const response = await configuracaoIAAPI.buscarPorId(id);
      
      if (response.data.success) {
        const parametro = response.data.data;
        setFormData({
          ia_system_prompt: parametro.ia_system_prompt || '',
          ia_tone: parametro.ia_tone || 'formal',
          emojis: parametro.emojis || 'LOW',
          responder_por_audio: parametro.responder_por_audio !== undefined ? parametro.responder_por_audio : true,
          grupo_resumo_conversa: parametro.grupo_resumo_conversa || '',
          agent_name: parametro.agent_name || ''
        });
        setClienteNome(parametro.cliente_nome || 'Cliente');
        setIdCliente(parametro.id_cliente);
      } else {
        setError('Erro ao carregar dados da configuração');
      }
    } catch (err) {
      console.error('Erro ao carregar configuração:', err);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoadingData(false);
    }
  };

  const carregarTones = async () => {
    try {
      const response = await configuracaoIAAPI.listarTones();
      if (response.data.success) {
        setTones(response.data.data);
      }
    } catch (err) {
      console.error('Erro ao carregar tones:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleToneChange = async (tone) => {
    handleInputChange('ia_tone', tone);
    
    // Auto-gerar prompt sempre que o tom mudar
    try {
      setLoadingPrompt(true);
      const response = await configuracaoIAAPI.gerarPrompt(tone, idCliente);
      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          ia_tone: tone,
          ia_system_prompt: response.data.data.prompt
        }));
      }
    } catch (err) {
      console.error('Erro ao gerar prompt:', err);
    } finally {
      setLoadingPrompt(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const dadosParaEnvio = {
        ia_system_prompt: formData.ia_system_prompt,
        ia_tone: formData.ia_tone,
        emojis: formData.emojis || 'LOW',
        responder_por_audio: formData.responder_por_audio,
        grupo_resumo_conversa: formData.grupo_resumo_conversa || null,
        agent_name: formData.agent_name || null
      };

      const response = await configuracaoIAAPI.atualizar(id, dadosParaEnvio);

      if (response.data.success) {
        setSuccess(response.data.message || 'Configuração atualizada com sucesso!');
        setTimeout(() => {
          navigate('/configuracao-ia');
        }, 1500);
      } else {
        setError(response.data.error || 'Erro ao salvar configuração');
      }
    } catch (err) {
      console.error('Erro ao salvar configuração:', err);
      
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Erro ao conectar com o servidor');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVoltar = () => {
    navigate('/configuracao-ia');
  };

  if (loadingData) {
    return (
      <Layout title="Carregando...">
        <Container>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
            <FiLoader style={{ fontSize: '2rem', animation: 'spin 1s linear infinite' }} />
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title="Personalizar IA">
      <Container>
        <Header>
          <BackButton onClick={handleVoltar}>
            <FiArrowLeft />
            Voltar
          </BackButton>
          <Title>
            Personalizar IA - {clienteNome}
          </Title>
        </Header>

        <Form onSubmit={handleSubmit}>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <InfoBox>
            <InfoIcon><FiCpu /></InfoIcon>
            <div>
              <strong>Personalize como a IA responde seus leads</strong>
              <p style={{ margin: '8px 0 0 0' }}>
                Configure o tom de voz, uso de emojis e outras opções para que a IA 
                atenda seus clientes de forma personalizada no WhatsApp.
              </p>
            </div>
          </InfoBox>

          <FormSection>
            <SectionTitle>
              <FiSettings />
              Configurações de Personalidade
            </SectionTitle>
            
            <FormGrid>
              <FormGroup>
                <Label htmlFor="agent_name">Nome da IA</Label>
                <Input
                  id="agent_name"
                  type="text"
                  placeholder="Ex: Ana, Carlos, Assistente"
                  value={formData.agent_name}
                  onChange={(e) => handleInputChange('agent_name', e.target.value)}
                  maxLength={10}
                />
                <HelpText>Nome que a IA usará para se identificar (máximo 10 caracteres)</HelpText>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="ia_tone">Tom de Voz</Label>
                <Select
                  id="ia_tone"
                  value={formData.ia_tone}
                  onChange={(e) => handleToneChange(e.target.value)}
                >
                  {tones.map((tone) => (
                    <option key={tone.value} value={tone.value}>
                      {tone.label}
                    </option>
                  ))}
                </Select>
                <HelpText>Define como a IA se comunica com seus clientes</HelpText>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="emojis">Uso de Emojis</Label>
                <Select
                  id="emojis"
                  value={formData.emojis}
                  onChange={(e) => handleInputChange('emojis', e.target.value)}
                >
                  <option value="NONE">Nenhum - Nunca usar emojis</option>
                  <option value="LOW">Baixo - 0-1 emoji ocasionalmente</option>
                  <option value="MEDIUM">Médio - 1-2 emojis em momentos-chave</option>
                  <option value="HIGH">Alto - 2+ emojis, tom bem informal</option>
                </Select>
                <HelpText>Quantidade de emojis nas respostas</HelpText>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="responder_por_audio">Responder por Áudio</Label>
                <Select
                  id="responder_por_audio"
                  value={formData.responder_por_audio ? 'true' : 'false'}
                  onChange={(e) => handleInputChange('responder_por_audio', e.target.value === 'true')}
                >
                  <option value="false">Não - Apenas texto</option>
                  <option value="true">Sim - Pode enviar áudios</option>
                </Select>
                <HelpText>A IA pode responder com mensagens de áudio</HelpText>
              </FormGroup>

              <FormGroupFull>
                <Label htmlFor="ia_system_prompt">Instruções da IA (System Prompt)</Label>
                <Textarea
                  id="ia_system_prompt"
                  placeholder="Instruções sobre como a IA deve se comportar..."
                  value={formData.ia_system_prompt}
                  onChange={(e) => handleInputChange('ia_system_prompt', e.target.value)}
                  rows={5}
                  disabled={loadingPrompt}
                />
                <HelpText>
                  {loadingPrompt ? 'Gerando prompt automaticamente...' : 'Instruções detalhadas para o comportamento da IA (gerado automaticamente ao selecionar o Tom de Voz)'}
                </HelpText>
              </FormGroupFull>
            </FormGrid>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <FiSettings />
              Configurações Adicionais
            </SectionTitle>
            
            <FormGrid>
              <FormGroup>
                <Label htmlFor="grupo_resumo_conversa">Grupo de Resumo de Conversa</Label>
                <Input
                  id="grupo_resumo_conversa"
                  type="text"
                  placeholder="5555999968677"
                  value={formData.grupo_resumo_conversa}
                  onChange={(e) => handleInputChange('grupo_resumo_conversa', e.target.value)}
                />
                <HelpText>Número do grupo para receber resumos das conversas</HelpText>
              </FormGroup>
            </FormGrid>
          </FormSection>

          <Actions>
            <Button type="button" onClick={handleVoltar}>
              Cancelar
            </Button>
            <Button $primary type="submit" disabled={loading}>
              <ButtonIcon>
                {loading ? <FiLoader /> : <FiSave />}
              </ButtonIcon>
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </Actions>
        </Form>
      </Container>
    </Layout>
  );
};

export default ConfiguracaoIAForm;
